import classnames from 'classnames'
import React from 'react'
import { SolutionCard } from '../../elements/SolutionCard'
import { Text } from '../../elements/Text'
import { Color, containerMaxWidth, gridGap, mq } from '../../utils'
import { useSpacing } from 'marketing-site/src/library/providers/spacingProvider'
import { IProps } from './index'

const gapHMargin = '12px'
const gapVMarginMobile = '50px'
const gapVMargin = '28px'

export function SolutionCards({
  headline,
  cardColor,
  solutionCards,
  bgColor,
  subheadline,
}: IProps): React.ReactElement {
  const spacing = useSpacing()
  const solutionClass = classnames('solution-cards', {
    'solution-cards--padding': !spacing,
  })

  return (
    <div className={solutionClass}>
      {headline && (
        <div className="headline">
          <Text size="heading-2" brand="default">
            {headline}
          </Text>
        </div>
      )}
      {subheadline && (
        <div className="subheadline">
          <Text size="heading-4" brand="default">
            {subheadline}
          </Text>
        </div>
      )}
      <ol className="content-area">
        {solutionCards.map((solutionCard, index) => {
          const wrapperClasses = classnames({
            'card-wrapper': true,
            'card-wrapper--even-cards-after-three':
              solutionCards.length % 2 === 0 && solutionCards.length > 3,
          })
          return (
            <li key={index} className={wrapperClasses}>
              <SolutionCard {...solutionCard} cardColor={cardColor} />
            </li>
          )
        })}
      </ol>
      <style jsx>
        {`
          .solution-cards {
            display: grid;
            grid-row-gap: ${gridGap.mobile};
            grid-template-columns: [left-gutter] 1fr [content] 12fr [right-gutter] 1fr;
            grid-template-rows: [top] auto [content] auto [bottom] auto;
            justify-items: center;
            background: ${bgColor ?? Color.White};
            &--padding {
              padding-top: ${bgColor ? '10em' : '0'};
              padding-bottom: ${bgColor ? '10em' : '0'};
            }
            .headline {
              grid-row: top;
              grid-column: content;
              display: block;
              text-align: center;
              margin-bottom: 60px;
              padding: 0 8em;
            }
            .subheadline {
              grid-row: top;
              grid-column: content;
              display: block;
              text-align: center;
              padding: 3em 0 1em;
              margin: auto;
              width: 100%;
              max-width: $container-max-width;
            }
            .content-area {
              grid-row: content;
              grid-column: content;
              max-width: ${containerMaxWidth};
              display: flex;
              flex-wrap: wrap;
              justify-content: center;
              margin: 0 -${gapHMargin} -${gapVMarginMobile};
              padding: 0;
            }
            .card-wrapper {
              flex: 0 1 30%;
              margin: 0 ${gapHMargin} ${gapVMarginMobile};
              justify-content: left;
              text-align: left;
            }
          }
          @media (${mq.mobile}) {
            .solution-cards {
              .headline {
                padding: 2em 0 0;
              }
              .subheadline {
                padding: 4em 0 1em;
              }
              .card-wrapper {
                flex: 1 1 30%;
              }
            }
          }
          @media (${mq.tablet}) {
            .content-area {
              margin: 0 -${gapHMargin} -${gapVMargin};
            }
            .subheadline {
              padding: 1em 0.75em;
            }
            .solution-cards {
              .card-wrapper {
                /* affects a 2-column layout with pleasantly oversized trailing item */
                display: flex;
                flex: 1 1 50%;
                max-width: 45%;
                margin: 0 ${gapHMargin} ${gapVMargin};
              }
            }
          }
          @media (${mq.desktop}) {
            .solution-cards {
              .card-wrapper {
                /* affects a 3-column layout with pleasantly oversized trailing items */
                flex: 0 1 30%;
                max-width: 40%;
              }

              .subheadline {
                padding: 1em 0;
                text-align: center;
              }
              .card-wrapper.card-wrapper--even-cards-after-three {
                flex-basis: 50%;
                max-width: 42.5%;
                margin: 0 12px 14px;
              }
            }
          }

          @media (${mq.desktopLg}) {
            .solution-cards {
              .subheadline {
                padding: 1em 0;
              }
              .card-wrapper {
                flex: 0 1 30%;
              }
            }
          }
          @media (${mq.desktopXLg}) {
            .solution-cards {
              .subheadline {
                padding: 1em 0.75em;
              }
              .card-wrapper {
                flex: 0 1 30%;
              }
            }
          }
        `}
      </style>
    </div>
  )
}
